import { ref, unref, watch, deepUnwrap } from 'vue'
import type { Ref, ComputedRef } from 'vue'

export function useReactiveQuery<T, F, P, S>(
  fetchFn: (filters: F, pagination: P, sort: S) => Promise<T[]>,
  initialFilters: F | Ref<F> | ComputedRef<F>,
  initialPagination?: P | Ref<P> | ComputedRef<P>,
  initialSort?: S | Ref<S> | ComputedRef<S>,
  cacheTrigger?: Ref<Date | undefined>
) {
  const data = ref<T[]>([])
  const loading = ref<boolean>(false)
  const error = ref<Error | null>(null)

  const fetchData = async () => {
    loading.value = true
    error.value = null
    try {
      // In Vue 3.3+, unref handles refs and getters/computed
      const filters = unref(initialFilters) as F
      const pagination = initialPagination
        ? (unref(initialPagination) as P)
        : undefined
      const sort = initialSort ? (unref(initialSort) as S) : undefined

      // Since pagination and sort are optional in the hook but required by fetchFn signature if passed,
      // we might need to cast or ensure fetchFn handles undefined.
      // Assuming the consumer passes valid structures matching fetchFn.
      data.value = await fetchFn(filters, pagination as P, sort as S)
    } catch (e) {
      error.value = e as Error
      console.error('Failed to fetch data:', e)
    } finally {
      loading.value = false
    }
  }

  const watchDeps = cacheTrigger
    ? [
        () => unref(initialFilters),
        () => unref(initialPagination),
        () => unref(initialSort),
        cacheTrigger,
      ]
    : [
        () => unref(initialFilters),
        () => unref(initialPagination),
        () => unref(initialSort),
      ]

  const stopWatch = watch(watchDeps, fetchData, { deep: true })

  const refetch = () => fetchData()

  // Initial fetch
  fetchData()

  return {
    data,
    loading,
    error,
    unsubscribe: stopWatch,
    refetch,
  }
}
